import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Play, Pause, CheckCircle2, Undo2, Plus, Trash2, Search, Wrench, Timer, ListOrdered, ClipboardList } from 'lucide-react';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';

const fmt = (s) => {
  const v = Math.max(0, Math.floor(s || 0));
  return `${String(Math.floor(v / 3600)).padStart(2, '0')}:${String(Math.floor((v % 3600) / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};
const short = (s) => (!s ? '—' : `${Math.floor(s / 60)}m${String(Math.floor(s % 60)).padStart(2, '0')}`);
const prioCls = { Alta: 't-bad', 'Média': 't-warn', Baixa: '' };
const srcLabel = { manual: 'Manual', repair_order: 'Repair Order', inspection: 'Inspeção de Entrada', delivery: 'Delivery Inspection', sold_review: 'Revisão dos Vendidos' };

const emptyForm = { vehicle_desc: '', stock: '', vin: '', cliente: '', servico: 'Alinhamento', prioridade: 'Média', notas: '', bay: '', vehicle_id: '' };

export const MechanicQueue = () => {
  const [board, setBoard] = useState({ bays: [], queue: [], done_today: [], stats: {} });
  const [cfg, setCfg] = useState({ bays: [], service_types: [], priorities: [] });
  const [cands, setCands] = useState([]);
  const [sub, setSub] = useState('add');
  const [form, setForm] = useState(emptyForm);
  const [vehicles, setVehicles] = useState([]);
  const [vq, setVq] = useState('');
  const [cq, setCq] = useState('');
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/mech/board'); setBoard(data); } catch (e) {}
  }, []);
  const loadCands = useCallback(async () => {
    try { const { data } = await api.get('/mech/candidates'); setCands(data.candidates || []); } catch (e) {}
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { loadCands(); }, [loadCands, board.queue?.length]);
  useEffect(() => { api.get('/mech/config').then((r) => setCfg(r.data)).catch(() => {}); api.get('/vehicles').then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  useEffect(() => { const iv = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(iv); }, []);

  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      await api.post(path, body || {});
      await load(); await loadCands();
      if (msg) toast({ title: msg });
    } catch (e) { toast({ title: 'Não foi possível', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  const addJob = async (payload, msg = 'Serviço adicionado à fila') => {
    setBusy(true);
    try {
      await api.post('/mech/jobs', payload);
      await load(); await loadCands();
      toast({ title: msg });
      return true;
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); return false; }
    finally { setBusy(false); }
  };

  const submitForm = async () => {
    if (!form.vehicle_desc.trim()) { toast({ title: 'Informe o veículo' }); return; }
    const ok = await addJob({ ...form, source: 'manual' });
    if (ok) { setForm(emptyForm); setVq(''); }
  };

  const remove = async (jid) => {
    try { await api.delete(`/mech/jobs/${jid}`); load(); loadCands(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const setPrio = async (jid, prioridade) => {
    try { await api.put(`/mech/jobs/${jid}`, { prioridade }); load(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const st = board.stats || {};
  const freeBays = (board.bays || []).filter((b) => !b.job);

  const vehicleHits = useMemo(() => {
    const s = vq.trim().toLowerCase();
    if (s.length < 2) return [];
    return vehicles.filter((v) => `${v.ano} ${v.marca} ${v.modelo} ${v.placa || ''} ${v.vin || ''}`.toLowerCase().includes(s)).slice(0, 6);
  }, [vq, vehicles]);

  const candHits = useMemo(() => {
    const s = cq.trim().toLowerCase();
    if (!s) return cands;
    return cands.filter((c) => `${c.vehicle_desc} ${c.cliente} ${c.stock} ${c.vin} ${c.servico}`.toLowerCase().includes(s));
  }, [cq, cands]);

  const BayCard = ({ b }) => {
    const j = b.job;
    const running = j?.status === 'in_progress';
    const elapsed = !j ? 0 : (j.accumulated_seconds || 0) + (running && j.resumed_at ? Math.floor((Date.now() - new Date(j.resumed_at).getTime()) / 1000) : 0);
    return (
      <div className={`ent-panel ${running ? 'border-primary/60' : ''}`} data-testid={`mq-bay-${b.key}`}>
        <div className="ent-panel-head">
          <Wrench className="w-3.5 h-3.5" />
          <span className="ent-label">{b.label}</span>
          <span className={`ent-pill ml-auto ${running ? 'border-primary text-primary' : j ? 't-warn' : ''}`}>
            {running ? 'EM EXECUÇÃO' : j ? (j.status === 'paused' ? 'PAUSADO' : 'AGUARDANDO INÍCIO') : 'LIVRE'}
          </span>
        </div>
        {!j ? (
          <div className="p-5 text-center">
            <p className="text-[12px] text-muted-foreground">Baia livre.</p>
            {board.queue?.length > 0 && (
              <select data-testid={`mq-bay-pick-${b.key}`} defaultValue="" onChange={(e) => e.target.value && act(`/mech/jobs/${e.target.value}/assign`, { bay: b.key }, 'Serviço posicionado na baia')}
                className="ent-input mt-3">
                <option value="">Trazer da fila…</option>
                {board.queue.map((q) => <option key={q.id} value={q.id} className="bg-popover text-foreground">{`${q.vehicle_desc} — ${q.servico}`}</option>)}
              </select>
            )}
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[13px] font-bold truncate" data-testid={`mq-bay-veh-${b.key}`}>{j.vehicle_desc}</p>
                <p className="text-[11px] t-label mono">{[j.stock && `#${j.stock}`, `VIN ${j.vin || '—'}`, srcLabel[j.source] || j.source].filter(Boolean).join(' · ')}</p>
              </div>
              <span className={`ent-tag ${prioCls[j.prioridade] || ''}`}>{j.prioridade}</span>
            </div>
            <p className="mt-2 text-[12px] text-foreground/80">{j.servico}{j.cliente ? ` · ${j.cliente}` : ''}</p>
            {j.notas && <p className="mt-1.5 border-l-2 border-primary/50 pl-2 text-[11px] text-muted-foreground">{j.notas}</p>}
            <p className={`dt-timer mt-3 text-4xl font-black leading-none ${running ? 'text-primary' : 'text-foreground/70'}`} data-testid={`mq-timer-${b.key}`}>{fmt(elapsed)}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {running ? (
                <button data-testid={`mq-pause-${b.key}`} disabled={busy} onClick={() => act(`/mech/jobs/${j.id}/pause`, {}, 'Cronômetro pausado')} className="ent-btn ent-btn-sm"><Pause className="w-3 h-3" /> Pausar</button>
              ) : (
                <button data-testid={`mq-start-${b.key}`} disabled={busy} onClick={() => act(`/mech/jobs/${j.id}/start`, { bay: b.key }, j.started_at ? 'Trabalho retomado' : 'Trabalho iniciado')} className="ent-btn ent-btn-primary ent-btn-sm"><Play className="w-3 h-3" /> {j.started_at ? 'Retomar' : 'Iniciar'}</button>
              )}
              <button data-testid={`mq-finish-${b.key}`} disabled={busy} onClick={() => act(`/mech/jobs/${j.id}/finish`, {}, 'Serviço finalizado')} className="ent-btn ent-btn-sm text-[hsl(var(--ok))] border-[hsl(var(--ok))]/50"><CheckCircle2 className="w-3 h-3" /> Finalizar</button>
              <button data-testid={`mq-unassign-${b.key}`} disabled={busy} onClick={() => act(`/mech/jobs/${j.id}/unassign`, {}, 'Serviço devolvido para a fila')} className="ent-btn ent-btn-sm"><Undo2 className="w-3 h-3" /> Devolver</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="mech-queue">
      <div className="ent-panel ent-ribbon" data-testid="mq-stats">
        <div className="px-4 py-3"><p className="ent-label">Na fila</p><p className="dt-timer mt-1 text-2xl font-black leading-none" data-testid="mq-stat-queue">{st.queue_count ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">demanda do dia</p></div>
        <div className="px-4 py-3"><p className="ent-label">Em execução</p><p className={`dt-timer mt-1 text-2xl font-black leading-none ${st.running ? 'text-primary' : ''}`}>{st.running ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">{st.paused ?? 0} pausado(s)</p></div>
        <div className="px-4 py-3"><p className="ent-label">Baias livres</p><p className="dt-timer mt-1 text-2xl font-black leading-none">{st.bays_free ?? 0}/3</p><p className="mt-1 text-[11px] text-muted-foreground/70">alinhamento · elevador · extra</p></div>
        <div className="px-4 py-3"><p className="ent-label">Prioridade alta</p><p className={`dt-timer mt-1 text-2xl font-black leading-none ${st.high_priority ? 't-bad' : ''}`}>{st.high_priority ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">na fila agora</p></div>
        <div className="px-4 py-3"><p className="ent-label">Finalizados hoje</p><p className="dt-timer mt-1 text-2xl font-black leading-none t-ok">{st.done_today ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">tempo {short(st.total_elapsed_today)}</p></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {(board.bays || []).map((b) => <BayCard key={b.key} b={b} />)}
      </div>

      <div className="ent-panel" data-testid="mq-queue-panel">
        <div className="ent-panel-head">
          <ListOrdered className="w-3.5 h-3.5" />
          <span className="ent-label">Fila de trabalho</span>
          <span className="ent-pill">{board.queue?.length || 0}</span>
          <span className="text-[11px] t-label ml-auto hidden sm:block">Ordenada por prioridade e ordem de entrada</span>
        </div>
        {!board.queue?.length ? (
          <p className="ent-empty">Fila vazia — nenhum serviço aguardando</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ent-table">
              <thead><tr><th>#</th><th>Veículo</th><th>Serviço</th><th>Origem</th><th>Prioridade</th><th>Adicionado por</th><th className="text-right">Ações</th></tr></thead>
              <tbody>
                {board.queue.map((j, i) => (
                  <tr key={j.id} data-testid={`mq-queue-${j.id}`}>
                    <td className="dt-timer t-label">{i + 1}</td>
                    <td><p className="font-semibold">{j.vehicle_desc}</p><p className="text-[10.5px] t-label mono">{[j.stock && `#${j.stock}`, j.cliente].filter(Boolean).join(' · ') || '—'}</p></td>
                    <td className="text-foreground/80">{j.servico}</td>
                    <td><span className="ent-tag">{srcLabel[j.source] || j.source}</span></td>
                    <td>
                      <select data-testid={`mq-prio-${j.id}`} value={j.prioridade} onChange={(e) => setPrio(j.id, e.target.value)} className={`ent-input ${prioCls[j.prioridade] || ''}`} style={{ width: 110 }}>
                        {(cfg.priorities || ['Alta', 'Média', 'Baixa']).map((p) => <option key={p} value={p} className="bg-popover text-foreground">{p}</option>)}
                      </select>
                    </td>
                    <td className="t-label text-[11px]">{j.created_by_name}</td>
                    <td>
                      <div className="flex items-center justify-end gap-1.5">
                        {freeBays.length > 0 && (
                          <select data-testid={`mq-send-${j.id}`} defaultValue="" onChange={(e) => e.target.value && act(`/mech/jobs/${j.id}/assign`, { bay: e.target.value }, 'Serviço posicionado na baia')} className="ent-input" style={{ width: 160 }}>
                            <option value="">Enviar para baia…</option>
                            {freeBays.map((b) => <option key={b.key} value={b.key} className="bg-popover text-foreground">{b.label}</option>)}
                          </select>
                        )}
                        <button data-testid={`mq-del-${j.id}`} onClick={() => remove(j.id)} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ent-tabs">
        <button data-testid="mq-sub-add" onClick={() => setSub('add')} className={`ent-tab flex items-center gap-1.5 ${sub === 'add' ? 'ent-tab-active' : ''}`}><Plus className="w-3.5 h-3.5" /> Adicionar serviço</button>
        <button data-testid="mq-sub-org" onClick={() => setSub('org')} className={`ent-tab flex items-center gap-1.5 ${sub === 'org' ? 'ent-tab-active' : ''}`}><ClipboardList className="w-3.5 h-3.5" /> Organizar fila</button>
      </div>

      {sub === 'add' ? (
        <div className="ent-panel" data-testid="mq-add-panel">
          <div className="ent-panel-head"><span className="ent-label">Novo serviço na fila</span></div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <p className="ent-label mb-1">Veículo</p>
              <div className="relative">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input data-testid="mq-form-veh" className="ent-input" style={{ paddingLeft: 24 }} placeholder="Buscar no estoque ou digitar livremente"
                  value={form.vehicle_desc || vq} onChange={(e) => { setVq(e.target.value); setForm({ ...form, vehicle_desc: e.target.value, vehicle_id: '', stock: '', vin: '' }); }} />
              </div>
              {vehicleHits.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 border border-border bg-popover max-h-52 overflow-y-auto">
                  {vehicleHits.map((v) => (
                    <button key={v.id} data-testid={`mq-veh-pick-${v.id}`} onClick={() => { setForm({ ...form, vehicle_id: v.id, vehicle_desc: `${v.ano} ${v.marca} ${v.modelo}`, stock: v.placa || '', vin: v.vin || '' }); setVq(''); }}
                      className="w-full text-left px-3 py-2 text-[12px] hover:bg-muted/50 flex items-center justify-between gap-2">
                      <span>{v.ano} {v.marca} {v.modelo}</span>
                      <span className="mono t-label text-[10.5px]">#{v.placa || '—'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div><p className="ent-label mb-1">Cliente (opcional)</p><input data-testid="mq-form-cliente" className="ent-input" value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} /></div>
            <div><p className="ent-label mb-1">Serviço</p>
              <select data-testid="mq-form-servico" className="ent-input" value={form.servico} onChange={(e) => setForm({ ...form, servico: e.target.value })}>
                {(cfg.service_types || []).map((s) => <option key={s} value={s} className="bg-popover text-foreground">{s}</option>)}
              </select>
            </div>
            <div><p className="ent-label mb-1">Prioridade</p>
              <select data-testid="mq-form-prio" className="ent-input" value={form.prioridade} onChange={(e) => setForm({ ...form, prioridade: e.target.value })}>
                {(cfg.priorities || []).map((p) => <option key={p} value={p} className="bg-popover text-foreground">{p}</option>)}
              </select>
            </div>
            <div><p className="ent-label mb-1">Baia (opcional)</p>
              <select data-testid="mq-form-bay" className="ent-input" value={form.bay} onChange={(e) => setForm({ ...form, bay: e.target.value })}>
                <option value="">Somente na fila</option>
                {freeBays.map((b) => <option key={b.key} value={b.key} className="bg-popover text-foreground">{b.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2"><p className="ent-label mb-1">Notas</p><input data-testid="mq-form-notas" className="ent-input" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Observações para o mecânico" /></div>
            <div className="md:col-span-4 flex justify-end">
              <button data-testid="mq-form-save" disabled={busy} onClick={submitForm} className="ent-btn ent-btn-primary h-9 px-5"><Plus className="w-3.5 h-3.5" /> Adicionar à fila</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="ent-panel" data-testid="mq-org-panel">
          <div className="ent-panel-head flex-wrap">
            <span className="ent-label">Veículos com Repair Order, Fila de Inspeção ou Delivery Inspection</span>
            <span className="ent-pill">{candHits.length}</span>
            <div className="ml-auto relative">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input data-testid="mq-cand-search" className="ent-input" style={{ width: 210, paddingLeft: 24 }} placeholder="Veículo, cliente, stock…" value={cq} onChange={(e) => setCq(e.target.value)} />
            </div>
          </div>
          {!candHits.length ? (
            <p className="ent-empty">Nenhum veículo disponível — todos já estão na fila ou nas baias</p>
          ) : (
            <div className="max-h-[420px] overflow-y-auto">
              <table className="ent-table">
                <thead><tr><th>Origem</th><th>Veículo</th><th>Cliente</th><th>Status</th><th>Serviço sugerido</th><th className="text-right">Ações</th></tr></thead>
                <tbody>
                  {candHits.map((c) => (
                    <tr key={`${c.source}-${c.source_id}`} data-testid={`mq-cand-${c.source_id}`}>
                      <td><span className="ent-tag">{srcLabel[c.source]}</span></td>
                      <td><p className="font-semibold">{c.vehicle_desc}</p><p className="text-[10.5px] t-label mono">{[c.stock && `#${c.stock}`, c.detalhe].filter(Boolean).join(' · ') || '—'}</p></td>
                      <td className="text-muted-foreground">{c.cliente || '—'}</td>
                      <td className="text-muted-foreground">{c.status || '—'}</td>
                      <td className="text-foreground/80">{c.servico}</td>
                      <td>
                        <div className="flex items-center justify-end gap-1.5">
                          <button data-testid={`mq-cand-add-${c.source_id}`} disabled={busy}
                            onClick={() => addJob({ ...c, prioridade: 'Média' })} className="ent-btn ent-btn-sm"><Plus className="w-3 h-3" /> Para a fila</button>
                          {freeBays.length > 0 && (
                            <select data-testid={`mq-cand-bay-${c.source_id}`} defaultValue="" onChange={(e) => e.target.value && addJob({ ...c, prioridade: 'Média', bay: e.target.value }, 'Serviço posicionado na baia')} className="ent-input" style={{ width: 160 }}>
                              <option value="">Direto para baia…</option>
                              {freeBays.map((b) => <option key={b.key} value={b.key} className="bg-popover text-foreground">{b.label}</option>)}
                            </select>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="ent-panel" data-testid="mq-done-panel">
        <div className="ent-panel-head"><Timer className="w-3.5 h-3.5" /><span className="ent-label">Finalizados hoje</span><span className="ent-pill">{board.done_today?.length || 0}</span></div>
        {!board.done_today?.length ? (
          <p className="ent-empty">Nenhum serviço finalizado hoje</p>
        ) : (
          <table className="ent-table">
            <thead><tr><th>Veículo</th><th>Serviço</th><th>Mecânico</th><th className="text-right">Tempo</th></tr></thead>
            <tbody>
              {board.done_today.map((j) => (
                <tr key={j.id} data-testid={`mq-done-${j.id}`}>
                  <td className="font-semibold">{j.vehicle_desc}</td>
                  <td className="text-muted-foreground">{j.servico}</td>
                  <td className="text-muted-foreground">{j.worker_name || '—'}</td>
                  <td className="dt-timer text-right font-bold">{short(j.duration_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MechanicQueue;
